import { DefaultValueConfig } from '@headless-workspace/config';
import { Image, ProductType } from '@headless-workspace/typings';
import { ObjectHelpers, ValueHelpers } from '@headless-workspace/utils';
import { mapURLToImage } from '../../../../src/client/lib/types';
import { mapProductPriceData, ProductBrandData, ProductPriceData, PromotionType } from '../types';
import { CapacityFormat } from './CapacityFormat';
import { ProductDetailSetItemDTO } from './ProductDetailSetItemDTO';
import { getPromotionKey } from './ProductDetailValue';
import { extractVariantFormats, mapProductDetailVariant, ProductDetailVariantValue } from './ProductDetailVariantValue';

export type ProductSetItemValue = ProductPriceData & {
    id: string;
    defaultVariantId?: string;
    name: string;
    brand: {
        id: string;
        name: string;
        url: string;
    };
    imageUrl: Image;
    type: ProductType;
    price?: number;
    pricePerUnit?: string;
    variants: ProductDetailVariantValue[];
    formats: CapacityFormat[];
    currency: string;
    promotion?: PromotionType;
};

export const mapProductDetailSetItem =
    (currency: string, brand: ProductBrandData['brand']) =>
    (dto: ProductDetailSetItemDTO): ProductSetItemValue => {
        let variants = dto.variants.map((variant) => mapProductDetailVariant(variant, currency));

        // In case of a product set with no defaultVariantId, we display only the variant which corresponds to the sku
        // If it doesn't exist (which should never happen) we display the first variant of the list as a fallback
        const defaultVariant: ProductDetailVariantValue =
            variants.find((variant) => variant.id === dto.id) || variants[0];

        if (!dto.defaultVariantId) {
            variants = [defaultVariant];
        }

        const prices = mapProductPriceData(dto, currency);

        return {
            id: dto.id,
            defaultVariantId: ValueHelpers.mapOptional(dto.defaultVariantId),
            name: dto.name,
            brand: {
                id: brand.id,
                name: brand.name,
                url: brand.url,
            },
            imageUrl:
                mapURLToImage(dto.imageUrl) ??
                ObjectHelpers.createImageWithSrc(DefaultValueConfig.imagePlaceholder, dto.name),
            type: ProductType[dto.type],
            pricePerUnit: ValueHelpers.mapOptional(dto.pricePerUnit),
            variants,
            formats: extractVariantFormats(variants),
            promotion: getPromotionKey(dto.promotion, prices) as PromotionType,
            ...prices,
        };
    };
